import { Location } from '@cobliteam/common-types';
import useChatWidget from '@cobliteam/hooks/src/useChatWidget';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags, { type FeatureFlags } from '@cobliteam/hooks/src/useFeatureFlags';
import useLogout from '@cobliteam/hooks/src/useSession/useLogout';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import AppHeader from '@cobliteam/ui-business/components/AppHeader';
import AppSidebar from '@cobliteam/ui-business/components/AppSidebar';
import { SidebarInset, SidebarProvider } from '@cobliteam/ui-business/components/AppSidebar/components/Sidebar';
import { Suspense, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import useRedirectAfterLogin from '../../hooks/useRedirectAfterLogin';
import { initializeThirdParty } from '../../libs/configureThirdParty';
import LoadingPage from '../../pages/LoadingPage';
import { useDashboard } from '../../providers/DashboardProvider';

function NoGroupsLayout() {
  const { runtimeFlags } = useDashboard();
  const { mutate: logout } = useLogout();
  const { setPosition } = useChatWidget();
  const history = useHistory();
  const { getSession, useGetUserData } = useUser();
  const { redirectSessionStorage } = useRedirectAfterLogin();
  const { ddRumSampleRate, ddRumReplaySampleRate, oneSignal, useDdRum } = useFeatureFlags();
  const session = getSession();
  const { data: userData } = useGetUserData();
  const currentFleet = session?.fleet;
  const fleets = userData?.userFleets;

  // Handle redirect after login
  useEffect(() => {
    const redirectUrl = redirectSessionStorage();
    if (redirectUrl) {
      history.push(redirectUrl);
    }
  }, [history, redirectSessionStorage]);

  // Initialize third-party services
  useEffect(() => {
    const featureFlags = {
      ddRumSampleRate,
      ddRumReplaySampleRate,
      oneSignal,
      useDdRum,
    } as FeatureFlags;
    initializeThirdParty({ session, userData, featureFlags });
  }, [session, userData, ddRumSampleRate, ddRumReplaySampleRate, oneSignal, useDdRum]);

  useEffect(() => {
    const hasDefaultTab = history.location.pathname === Location.MONITORING || history.location.pathname === Location.TRACKING;
    setPosition(hasDefaultTab ? 'tab' : 'default');
  }, [history.location.pathname, setPosition]);

  return (
    <div>
      <SidebarProvider>
        <AppSidebar fleets={fleets || []} currentFleet={currentFleet} />
        <SidebarInset>
          <AppHeader user={session.user} logout={() => logout()} />
          <Suspense fallback={<LoadingPage />}>
            <div className='flex h-full flex-col items-center justify-center'>
              <div className='mb-4 rounded-full bg-gray-4 p-4'>
                <Icon name='folder-x' size='lg' />
              </div>
              <Typography variant='heading-xl' color='secondary' bold className='mb-1'>
                {runtimeFlags.hasOnlyGroupsWithNoVehicles ? 'Você não está em nenhum grupo com veículos' : 'Você não está adicionado a nenhum grupo'}
              </Typography>
              <Typography variant='text-lg' color='secondary' className='max-w-[500px] text-center'>
                Entre em contato com seu gestor e solicite a sua adição a um grupo com veículos para acessar os dados do painel
              </Typography>
            </div>
          </Suspense>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
}

export default NoGroupsLayout;
